import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  ApiService,
  FormTypeEnum,
  SystemFormExtraActionsEnum,
  ViewTypeEnum,
} from "../../api";
import { Button, Col, Collapse, Space, Steps } from "antd";
import { StepProps } from "antd/lib";
import { CreateStepType } from "../../constants";
import WorkflowViewPage from "./WorkflowViewPage";
import RenderFormComponent from "../../components/RenderFormComponent";
import { CaretRightOutlined } from "@ant-design/icons";
import { CreateStepsProps } from "./EditRequestPage";
import { useCloseOwnTab } from "../../tabs/tabContext";

export type CreateFormsProps = {
  form_id?: number;
  schema?: any;
  form?: any;
  workflow_id?: number;
  type: CreateStepType;
  formType?: FormTypeEnum;
  system_form_extra_actions?: any;
} & StepProps;

const CreateRequestPage = () => {
  const { t } = useTranslation();
  const { shortName } = useParams();
  const { state } = useLocation();

  const location = useNavigate();
  const closeOwnTab = useCloseOwnTab();
  const [viewType, setViewType] = useState<ViewTypeEnum>(ViewTypeEnum.COLLAPSE);
  const [current, setCurrent] = useState<number>(0);
  const [name, setName] = useState("");
  const [items, setItems] = useState<CreateFormsProps[]>([]);
  const [requestId, setRequestId] = useState<number | undefined>(undefined);
  const [duplicateRequestId, setDuplicateRequestId] = useState<
    number | undefined
  >(undefined);

  const prev = () => {
    setCurrent(current - 1);
  };

  const sendForReview = async () => {
    await ApiService.apiMainGenerateDocumentRetrieve(requestId!);
    // The request is created; close this Create tab before navigating so the
    // user is left on the request itself rather than an empty create form.
    closeOwnTab();
    location("/requests/" + requestId);
  };

  const handleSuccessSubmit = async (result: any) => {
    setRequestId(result.request_id);
    if (current === items.length - 1) {
      sendForReview();
    } else {
      setCurrent(current + 1);
    }
  };

  useEffect(() => {
    console.log("asd");
    if (state !== null) {
      setDuplicateRequestId(state.requestId);
      (async () => {
        await ApiService.apiMainRequestsRetrieve(
          parseInt(state.requestId!)
        ).then((result) => {
          setViewType(result.template.view_type ?? ViewTypeEnum.COLLAPSE);
          const items: CreateStepsProps[] = result.template.form_templates.map(
            (ft: any, index) => {
              return {
                key: index,
                title: t(ft.title),
                form_id: ft.id,
                form: ft.schema_json.form,
                schema: ft.schema_json.schema,
                type: CreateStepType.Form,
                formType: ft.type,
                system_form_extra_actions: ft.system_form_extra_actions,
              };
            }
          );

          const workflows: CreateStepsProps[] = result.template.workflows.map(
            (ft, index) => {
              return {
                key: index,
                title: t(ft.name),
                workflow_id: ft.id,
                type: CreateStepType.Workflow,
              };
            }
          );

          console.log(items);

          setItems([...items, ...workflows]);
        });
      })();
    } else {
      (async () => {
        await ApiService.apiMainTemplatesRetrieve(shortName!).then((result) => {
          setViewType(result.view_type ?? ViewTypeEnum.COLLAPSE);
          setName(result.name);

          const items: CreateFormsProps[] = result.form_templates.map(
            (ft: any, index) => {
              return {
                key: index,
                title: t(ft.title),
                form_id: ft.id,
                schema: ft.schema_json.schema,
                form: ft.schema_json.form,
                type: CreateStepType.Form,
                formType: ft.type,
                system_form_extra_actions: ft.system_form_extra_actions,
              };
            }
          );

          const workflows: CreateFormsProps[] = result.workflows.map(
            (ft, index) => {
              return {
                key: index,
                title: t(ft.name),
                workflow_id: ft.id,
                type: CreateStepType.Workflow,
              };
            }
          );

          setItems([...items, ...workflows]);
        });
      })();
    }
  }, []);

  const renderView = () => {
    if (items.length === 0) {
      return null;
    }

    if (items[current].type === CreateStepType.Form) {
      return (
        <RenderFormComponent
          form={items[current]}
          requestId={requestId}
          onSuccess={handleSuccessSubmit}
          duplicateRequestId={duplicateRequestId}
          setDuplicateRequestId={setDuplicateRequestId}
        />
      );
    }

    return (
      <WorkflowViewPage
        requestId={requestId}
        workflowId={items[current].workflow_id}
        onSuccess={handleSuccessSubmit}
      />
    );
  };

  if (viewType === ViewTypeEnum.STEP) {
    return (
      <div>
        <h4>{t("Create {{name}}", { name })}</h4>
        <Steps current={current} items={items} />
        <div style={{ marginTop: 24 }}>{renderView()}</div>
        <div style={{ marginTop: 24 }}>
          {current > 0 && (
            <Button style={{ margin: "0 8px" }} onClick={() => prev()}>
              {t("Previous")}
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div>
      <h4>{t("Create {{name}}", { name })}</h4>
      <Space direction="vertical" style={{ width: "100%" }}>
        {items.map((page) => (
          <Collapse
            expandIcon={({ isActive }) => (
              <CaretRightOutlined rotate={isActive ? 90 : 0} />
            )}
            ghost={true}
            items={[
              {
                key: page.form_id,
                label: page.title,
                children:
                  page.type === CreateStepType.Form ? (
                    <RenderFormComponent
                      form={page}
                      requestId={requestId}
                      onSuccess={handleSuccessSubmit}
                      duplicateRequestId={duplicateRequestId}
                      setDuplicateRequestId={setDuplicateRequestId}
                    />
                  ) : (
                    <WorkflowViewPage
                      requestId={requestId}
                      workflowId={page.workflow_id}
                      onSuccess={handleSuccessSubmit}
                      duplicateRequestId={duplicateRequestId}
                      setDuplicateRequestId={setDuplicateRequestId}
                    />
                  ),
              },
            ]}
          />
        ))}
      </Space>
      <Col span={24} style={{ textAlign: "center", marginTop: 24 }}>
        <Button
          style={{ margin: "auto" }}
          type="primary"
          onClick={() => sendForReview()}
        >
          {t("Review")}
        </Button>
      </Col>
    </div>
  );
};

export default CreateRequestPage;
